import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import { SupplierFormValues } from "../../../../../support/admin/entities/supplier/supplierTypes"
import {
    AgreementOptions,
    BusinessType,
    EntityHistory,
    EntityType,
} from "../../../../../support/admin/entities/supportEntitiesTypes"
import React, { Suspense, useRef } from "react"
import { FormikProps } from "formik"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { getAddress, getContactDetails, getInvoiceDetails } from "../../../../../support/admin/entities/helpers"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { SUPPLIER_ADMIN_ROUTE } from "../../../../../../constants/routes"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { useSupplier } from "../../../../supplierHooks"
import { fetchSupplierById, updateAccountApi } from "../accountApi"
import { AccountFromValues, SupplierAccountWithHistory } from "../accountTypes"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { AccountUpdateForm } from "../components/AccountUpdateForm"
import { SupportEntityQuery } from "../../../../../support/admin/entities/supportEntityApi"

type EntityWithHistory = Readonly<AccountFromValues & { history: EntityHistory }>

function transformData(data: SupplierAccountWithHistory): EntityWithHistory {
    const {
        isActive,
        businessNumber,
        name,
        businessType,
        contactDetails,
        address,
        isSystemOwner,
        history,
        invoiceDetails,
    } = data.supplier

    return {
        name,
        businessNumber,
        businessType,
        isActive,
        history,
        isSystemOwner: AgreementOptions[isSystemOwner ? "YES" : "NO"],
        email: contactDetails.email,
        contactPerson: contactDetails.name,
        telephone: contactDetails.phone,
        city: address.city,
        postalCode: address.postalCode,
        countryCode: address.countryCode,
        address1: address.addressLines[0],
        address2: address.addressLines[1],
        attention: invoiceDetails?.attention,
        eanNumber: invoiceDetails?.endpoint.eanNumber,
        invoiceEmail: invoiceDetails?.endpoint.email,
        invoiceType: invoiceDetails?.endpoint.type,
        poNumber: invoiceDetails?.poNumber,
        reminderEmail: invoiceDetails?.reminderEmail,
    }
}

export function UpdateSupplierAccount() {
    const accountFormRef = useRef<FormikProps<AccountFromValues>>(null)
    const { supplierId } = useSupplier()

    const supplier = useApiQuery(fetchSupplierById, {
        queryName: "suppliers-for-support",
        dependencies: {
            supplierId,
        },
        select: transformData,
    })

    const { mutate } = useApiMutation(updateAccountApi, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, __, context) {
            await context.queryClient.invalidateQueries({ queryKey: [SupportEntityQuery.LIST] })
        },
    })

    const handleSaveClick = () => {
        if (accountFormRef.current) {
            accountFormRef.current?.submitForm()
        }
    }

    const updateAccount = (value: SupplierFormValues) => {
        mutate({
            supplierId: supplierId,
            supplier: {
                type: EntityType.SUPPLIER,
                name: value.name,
                isSystemOwner: value.isSystemOwner === AgreementOptions.YES,
                businessNumber: value.businessNumber,
                businessType: value.businessType as BusinessType,
                address: getAddress(value),
                contactDetails: getContactDetails(value),
                ...getInvoiceDetails(value),
                isActive: value.isActive,
            },
        })
    }

    return (
        <>
            <LocalizedStrict id="supplier-update-account-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPLIER_UPDATE_ACCOUNT}
                    title="Supplier account"
                    icon={<FontAwesomeIcon icon={faShop} />}
                    rightElement={
                        <LocalizedStrict id="supplier-update-account-update-button">
                            <StandardButton onClick={handleSaveClick}>
                                Update
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="supplier-update-account-nav-item-home">
                            <BreadcrumbLink to={SUPPLIER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-update-account-nav-item-account">
                            <Breadcrumb>Account</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-company-account">
                            <Breadcrumb>Company account</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <AccountUpdateForm
                        history={supplier.data?.history}
                        initialValues={supplier.data}
                        ref={accountFormRef}
                        onSubmit={updateAccount}
                    />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
